import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { SecurityGroup, SecurityGroupCreateUpdate } from '@shared/models/security-group';
import { Observable, map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SecurityGroupsService {
  url = environment.api + 'securitygroups';
  elementUrl = environment.api + 'elements';

  private currentUserSecurityGroups: SecurityGroup[];

  private readonly http = inject(HttpClient);

  get() {
    return this.http.get<SecurityGroup[]>(this.url);
  }

  getById(id: number): Observable<SecurityGroup> {
    const requestUrl = `${this.url}/${id}`;
    return this.http.get<SecurityGroup>(requestUrl);
  }

  getByElementId(elementId: number): Observable<SecurityGroup[]> {
    const requestUrl = `${this.elementUrl}/${elementId}/securitygroups`;
    return this.http.get<SecurityGroup[]>(requestUrl);
  }

  post(securityGroupUpdateData: SecurityGroupCreateUpdate): Observable<SecurityGroup> {
    return this.http.post<SecurityGroup>(this.url, securityGroupUpdateData);
  }

  update(
    securityGroupUpdateData: SecurityGroupCreateUpdate,
    securityGroupId: number
  ): Observable<SecurityGroup> {
    const requestUrl = `${this.url}/${securityGroupId}`;
    return this.http.put<SecurityGroup>(requestUrl, securityGroupUpdateData);
  }

  delete(sgid: number) {
    const requestUrl = `${this.url}/${sgid}`;
    return this.http.delete<SecurityGroup>(requestUrl);
  }

  getSecurityGroupsForCurrentUser(): Observable<SecurityGroup[]> {
    // if we have previously retrieved the security groups for this user, return the previous result
    if (this.currentUserSecurityGroups) {
      return of(this.currentUserSecurityGroups);
    }

    // get and store the security groups for this user
    return this.http
      .get<SecurityGroup[]>(`${this.url}/currentUser`)
      .pipe(tap(res => (this.currentUserSecurityGroups = res)));
  }

  getSecurityGroupsForCurrentUserAndElementId(elementId: number): Observable<SecurityGroup[]> {
    // if we have previously retrieved the security groups for this user, return the previous results filtered to the requested element
    if (this.currentUserSecurityGroups) {
      return of(
        this.currentUserSecurityGroups.filter(securityGroup => securityGroup.elementId === elementId)
      );
    }

    // get the current user's security groups and return them filtered to the requested element
    return this.getSecurityGroupsForCurrentUser().pipe(
      map(securityGroups => securityGroups.filter(securityGroup => securityGroup.elementId === elementId))
    );
  }
}
